import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Button,
  Card,
  Col,
  Empty,
  message,
  Row,
  Select,
  Spin,
  Table,
  Typography,
} from "antd";

import {
  ReloadOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";

import "../../css/sm-art-common.css";
import CriticalOperationService from "../../services/CriticalOperationService";

const { Title, Text } = Typography;
const { Option } = Select;

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

const IncentiveLineReport = () => {
  const isMountedRef = useRef(true);

  const [lines, setLines] = useState([]);
  const [selectedLine, setSelectedLine] = useState(undefined);

  const [reportData, setReportData] = useState([]);

  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_LIMIT);
  const [totalRecords, setTotalRecords] = useState(0);

  const [loading, setLoading] = useState(false);
  const [lineLoading, setLineLoading] = useState(false);

  const dropdownSpinner = (
    <div style={{ textAlign: "center", padding: 8 }}>
      <Spin size="small" />
    </div>
  );

  const toNumber = (value) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  };

  const formatNumber = (value) =>
    toNumber(value).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  const calculateEfficiency = (target, actual) => {
    const targetValue = toNumber(target);
    const actualValue = toNumber(actual);

    if (targetValue <= 0) {
      return 0;
    }

    return (actualValue / targetValue) * 100;
  };

  const normalizeRows = useCallback((rows = []) => {
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((item, index) => {
      const realTimeTarget = toNumber(
        item?.real_time_target ??
          item?.realtime_target ??
          item?.["Real time Target"] ??
          item?.target_pcs ??
          0
      );

      const realTimeActual = toNumber(
        item?.real_time_actual ??
          item?.realtime_actual ??
          item?.operation_count ??
          item?.actual_pcs ??
          item?.count_pcs ??
          0
      );

      const apiEfficiency =
        item?.efficiency ??
        item?.efficiency_percentage ??
        item?.efficiency_percent;

      const efficiency =
        apiEfficiency !== undefined &&
        apiEfficiency !== null &&
        apiEfficiency !== ""
          ? toNumber(apiEfficiency)
          : calculateEfficiency(realTimeTarget, realTimeActual);

      return {
        ...item,

        row_key:
          item?.com_id ??
          item?.id ??
          item?.report_id ??
          `${item?.line_no || "line"}-${item?.op_id || index}-${index}`,

        line_no:
          item?.line_no ??
          item?.line_number ??
          item?.line ??
          "-",

        operation:
          item?.operation ??
          item?.operation_name ??
          item?.op_name ??
          "-",

        machine_type:
          item?.machine_type ??
          item?.machine_name ??
          item?.device_type ??
          item?.device_name ??
          item?.device_id ??
          "-",

        operator_name:
          item?.operator_name ??
          item?.staff_name ??
          item?.employee_name ??
          item?.user_name ??
          item?.operator ??
          "-",

        real_time_target: realTimeTarget,
        real_time_actual: realTimeActual,
        efficiency,
      };
    });
  }, []);

  const extractRows = (responseBody) => {
    const rowSources = [
      responseBody?.data?.incentive_line_report,
      responseBody?.data?.incentive_report,
      responseBody?.data?.report,
      responseBody?.data?.reports,
      responseBody?.data?.rows,
      responseBody?.data?.data,
      responseBody?.data,
      responseBody?.rows,
      responseBody?.report,
    ];

    return rowSources.find((item) => Array.isArray(item)) || [];
  };

  const extractTotalRecords = (responseBody, rows) => {
    const totalSources = [
      responseBody?.data?.pagination?.total_records,
      responseBody?.data?.pagination?.total,
      responseBody?.data?.pagination?.totalRecords,
      responseBody?.data?.total_records,
      responseBody?.data?.total,
      responseBody?.data?.totalRecords,
      responseBody?.pagination?.total_records,
      responseBody?.pagination?.total,
      responseBody?.total_records,
      responseBody?.total,
    ];

    const totalValue = totalSources.find(
      (value) =>
        value !== undefined &&
        value !== null &&
        value !== ""
    );

    return totalValue !== undefined
      ? toNumber(totalValue)
      : rows.length;
  };

  const loadLines = useCallback(async () => {
    setLineLoading(true);

    try {
      const response = await CriticalOperationService.getLines();

      if (!isMountedRef.current) {
        return;
      }

      if (!response?.ok) {
        setLines([]);
        return;
      }

      const responseBody = response?.data;

      const lineList =
        responseBody?.line_no ??
        responseBody?.data?.line_no ??
        responseBody?.data?.lines ??
        responseBody?.data ??
        [];

      setLines(Array.isArray(lineList) ? lineList : []);
    } catch (error) {
      console.error("Line API error:", error);

      if (isMountedRef.current) {
        setLines([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLineLoading(false);
      }
    }
  }, []);

  const loadReport = useCallback(
    async (
      page = DEFAULT_PAGE,
      limit = DEFAULT_LIMIT,
      lineNo = ""
    ) => {
      setLoading(true);

      try {
        const response =
          await CriticalOperationService.getIncentiveLineReport(
            lineNo,
            page,
            limit
          );

        if (!isMountedRef.current) {
          return;
        }

        if (!response?.ok) {
          setReportData([]);
          setTotalRecords(0);

          message.error(
            response?.data?.message ||
              "Incentive line report API is currently unavailable"
          );

          return;
        }

        const responseBody = response?.data;

        if (responseBody?.status === false) {
          setReportData([]);
          setTotalRecords(0);

          message.info(
            responseBody?.message ||
              "No incentive line report data found"
          );

          return;
        }

        const rows = extractRows(responseBody);
        const normalizedRows = normalizeRows(rows);

        setReportData(normalizedRows);

        setTotalRecords(
          extractTotalRecords(responseBody, normalizedRows)
        );
      } catch (error) {
        console.error(
          "Incentive line report API error:",
          error
        );

        if (isMountedRef.current) {
          setReportData([]);
          setTotalRecords(0);

          message.error(
            "Incentive line report API is currently unavailable"
          );
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [normalizeRows]
  );

  useEffect(() => {
    isMountedRef.current = true;

    loadLines();
    loadReport(DEFAULT_PAGE, DEFAULT_LIMIT, "");

    return () => {
      isMountedRef.current = false;
    };
  }, [loadLines, loadReport]);

  const handleSearch = () => {
    setCurrentPage(DEFAULT_PAGE);

    loadReport(
      DEFAULT_PAGE,
      pageSize,
      selectedLine || ""
    );
  };

  const handleReset = () => {
    setSelectedLine(undefined);
    setCurrentPage(DEFAULT_PAGE);
    setPageSize(DEFAULT_LIMIT);

    loadReport(DEFAULT_PAGE, DEFAULT_LIMIT, "");
  };

  const handleTableChange = (pagination) => {
    const nextPage =
      pagination?.current || DEFAULT_PAGE;

    const nextLimit =
      pagination?.pageSize || DEFAULT_LIMIT;

    setCurrentPage(nextPage);
    setPageSize(nextLimit);

    loadReport(
      nextPage,
      nextLimit,
      selectedLine || ""
    );
  };

  const getEfficiencyClass = (efficiency) => {
    const value = toNumber(efficiency);

    if (value >= 100) {
      return "sm-pill sm-pill-success";
    }

    if (value >= 75) {
      return "sm-pill sm-pill-info";
    }

    return "sm-pill sm-pill-danger";
  };

  const columns = useMemo(
    () => [
      {
        title: "Line No.",
        dataIndex: "line_no",
        key: "line_no",
        align: "center",
        width: 130,
        fixed: "left",
        render: (value) => value || "-",
      },
      {
        title: "Operation",
        dataIndex: "operation",
        key: "operation",
        width: 220,
        render: (value) => value || "-",
      },
      {
        title: "Machine Type",
        dataIndex: "machine_type",
        key: "machine_type",
        width: 180,
        render: (value) => value || "-",
      },
      {
        title: "Operator Name",
        dataIndex: "operator_name",
        key: "operator_name",
        width: 200,
        render: (value) => value || "-",
      },
      {
        title: "Real-Time Target",
        dataIndex: "real_time_target",
        key: "real_time_target",
        align: "center",
        width: 170,
        render: (value) => (
          <strong>{formatNumber(value)}</strong>
        ),
      },
      {
        title: "Real-Time Actual",
        dataIndex: "real_time_actual",
        key: "real_time_actual",
        align: "center",
        width: 170,
        render: (value) => (
          <strong>{formatNumber(value)}</strong>
        ),
      },
      {
        title: "Efficiency (%)",
        dataIndex: "efficiency",
        key: "efficiency",
        align: "center",
        width: 160,
        fixed: "right",
        render: (value) => {
          const efficiency = toNumber(value);

          return (
            <span className={getEfficiencyClass(efficiency)}>
              {efficiency.toFixed(2)}%
            </span>
          );
        },
      },
    ],
    []
  );

  const pageTotals = useMemo(() => {
    const totalTarget = reportData.reduce(
      (sum, row) =>
        sum + toNumber(row?.real_time_target),
      0
    );

    const totalActual = reportData.reduce(
      (sum, row) =>
        sum + toNumber(row?.real_time_actual),
      0
    );

    const efficiency =
      totalTarget > 0
        ? (totalActual / totalTarget) * 100
        : 0;

    return {
      totalTarget,
      totalActual,
      efficiency,
    };
  }, [reportData]);

  return (
    <div className="sm-page">
      <style>{`
        .incentive-report-filter .ant-select {
          width: 100%;
        }

        .sm-select.ant-select .ant-select-arrow,
        .sm-select.ant-select .ant-select-suffix,
        .sm-select.ant-select .ant-select-arrow *,
        .sm-select.ant-select .ant-select-suffix * {
          color: #000 !important;
          fill: #000 !important;
        }
      `}</style>

      <div className="sm-page-inner">
        <div className="sm-header">
          <div className="sm-title-wrap">
            <Title level={3} className="sm-page-title">
              <ThunderboltOutlined
                style={{ color: "#2150FD" }}
              />

              Incentive Line Report
            </Title>

            <Text className="sm-page-subtitle">
              View line-wise operator targets, production
              and efficiency.
            </Text>
          </div>
        </div>

        <Card
          className="sm-card"
          title={<span>Filters</span>}
          extra={
            <div className="sm-filter-actions">
              <Button
                className="sm-btn-soft"
                icon={<ReloadOutlined />}
                onClick={handleReset}
                disabled={loading}
              >
                Reset
              </Button>

              <Button
                className="sm-btn-primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
              >
                Search
              </Button>
            </div>
          }
        >
          <Row
            gutter={[16, 16]}
            className="incentive-report-filter"
          >
            <Col xs={24} sm={12} md={8}>
              <label className="sm-label">
                Line No.
              </label>

              <Select
                className="sm-select"
                placeholder={
                  lineLoading
                    ? "Loading lines..."
                    : "Select line"
                }
                value={selectedLine}
                onChange={setSelectedLine}
                allowClear
                showSearch
                optionFilterProp="children"
                loading={lineLoading}
                notFoundContent={
                  lineLoading
                    ? dropdownSpinner
                    : "No lines found"
                }
              >
                {lines.map((item, index) => {
                  const lineValue =
                    typeof item === "string"
                      ? item
                      : item?.line_no ??
                        item?.line_number ??
                        item?.name ??
                        item?.id;

                  if (
                    lineValue === undefined ||
                    lineValue === null
                  ) {
                    return null;
                  }

                  return (
                    <Option
                      key={`${lineValue}-${index}`}
                      value={String(lineValue)}
                    >
                      {String(lineValue)}
                    </Option>
                  );
                })}
              </Select>
            </Col>
          </Row>
        </Card>

        <Card
          className="sm-card"
          title={<span>Report Details</span>}
          style={{ marginTop: 16 }}
        >
          <Spin spinning={loading}>
            <div className="sm-table-wrap">
              <Table
                className="sm-table"
                columns={columns}
                dataSource={reportData}
                rowKey={(record, index) =>
                  record?.row_key ||
                  `incentive-row-${index}`
                }
                bordered
                sticky
                size="middle"
                scroll={{
                  x: 1300,
                  y: 480,
                }}
                locale={{
                  emptyText: (
                    <div className="sm-empty-wrap">
                      <Empty
                        description={
                          <span className="sm-empty-text">
                            No incentive line report data found.
                          </span>
                        }
                      />
                    </div>
                  ),
                }}
                pagination={{
                  current: currentPage,
                  pageSize,
                  total: totalRecords,
                  showSizeChanger: true,
                  pageSizeOptions: [
                    "10",
                    "20",
                    "50",
                    "100",
                  ],
                  showQuickJumper: true,
                  position: ["bottomCenter"],
                  className: "sm-pagination",
                  showTotal: (total, range) =>
                    total > 0
                      ? `${range[0]}-${range[1]} of ${total} records`
                      : "0 records",
                }}
                onChange={handleTableChange}
                summary={
                  reportData.length > 0
                    ? () => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell
                            index={0}
                            colSpan={4}
                            align="right"
                          >
                            <strong>Page Total</strong>
                          </Table.Summary.Cell>

                          <Table.Summary.Cell
                            index={4}
                            align="center"
                          >
                            <strong>
                              {formatNumber(
                                pageTotals.totalTarget
                              )}
                            </strong>
                          </Table.Summary.Cell>

                          <Table.Summary.Cell
                            index={5}
                            align="center"
                          >
                            <strong>
                              {formatNumber(
                                pageTotals.totalActual
                              )}
                            </strong>
                          </Table.Summary.Cell>

                          <Table.Summary.Cell
                            index={6}
                            align="center"
                          >
                            <strong>
                              {pageTotals.efficiency.toFixed(2)}
                              %
                            </strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )
                    : undefined
                }
              />
            </div>
          </Spin>
        </Card>
      </div>
    </div>
  );
};

export default IncentiveLineReport;